/**
 * All-day detection for `/calendar_events` rows.
 *
 * The wire carries no all-day flag — the BE emits
 * `(item.start.date_time || item.start.date)`, so a provider all-day event
 * arrives as an ordinary timed event. We infer it from the local-day span.
 *
 * Lives here rather than in a feature folder because both InterviewManager
 * calendar trees consume it and `ScheduleAll` may not import from
 * `ScheduleInterview` — one definition, no drift.
 */
import moment from 'moment-timezone';

export interface AllDayCandidate {
  start: string | number;
  end: string | number;
}

/**
 * Slack allowed at each edge, in ms. Covers the three endings we actually
 * see: the BE's `11:59 PM` (60s short of midnight), `splitEventAcrossDays`
 * chunks clamped to `endOf('day')` (1ms short), and true next-day midnight.
 */
const EDGE_TOLERANCE_MS = 60_000;

/** True when the event covers a whole local day in `timezone`. */
export function isAllDayEvent(evt: AllDayCandidate, timezone: string): boolean {
  const tz = timezone || moment.tz.guess();
  const start = moment.tz(evt.start, tz);
  const end = moment.tz(evt.end, tz);
  if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) {
    return false;
  }

  const dayStart = start.clone().startOf('day');
  const nextDayStart = dayStart.clone().add(1, 'day');

  return (
    start.diff(dayStart) <= EDGE_TOLERANCE_MS &&
    nextDayStart.diff(end) <= EDGE_TOLERANCE_MS
  );
}

/**
 * Split events into the ones that belong in a day-header chip and the ones
 * that stay in the time grid. Order is preserved within each bucket.
 */
export function partitionAllDayEvents<T extends AllDayCandidate>(
  events: T[],
  timezone: string
): { allDay: T[]; timed: T[] } {
  const allDay: T[] = [];
  const timed: T[] = [];
  for (const evt of events) {
    if (isAllDayEvent(evt, timezone)) {
      allDay.push(evt);
    } else {
      timed.push(evt);
    }
  }
  return { allDay, timed };
}
